"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_BASE_BINDING_WINDOW_ROWS = void 0;
exports.appBaseBindingQueryRows = appBaseBindingQueryRows;
exports.selectAppBaseBindingListFilters = selectAppBaseBindingListFilters;
exports.appBaseBindingLiteralFilterQuery = appBaseBindingLiteralFilterQuery;
exports.readAppBaseBindingLiteralLists = readAppBaseBindingLiteralLists;
exports.appBaseBindingRowFilterKeys = appBaseBindingRowFilterKeys;
exports.readAppBaseBindingDatasets = readAppBaseBindingDatasets;
exports.readAppBaseBindingCounts = readAppBaseBindingCounts;
/** Maximum rows in one binding window or literal-list union. */
exports.APP_BASE_BINDING_WINDOW_ROWS = 100;
/** Rows one query of a source returns: the window, or the smaller row limit of its binding. */
function appBaseBindingQueryRows(binding) {
    return Math.min(exports.APP_BASE_BINDING_WINDOW_ROWS, binding?.exposure.rowLimit ?? exports.APP_BASE_BINDING_WINDOW_ROWS);
}
/** The shared read plan for literal keys and row-dependent lists.
 * Selected sources keep their own window beside exact literal rows.
 * Passed to serialized readers, so this function has no runtime imports. */
function selectAppBaseBindingListFilters(filters, sources, selectedSources) {
    const filterSources = new Set(filters.flatMap(filter => filter.filterSourceKey ?? []));
    return filters
        .filter(filter => sources.includes(filter.sourceKey) &&
        (filter.filterSourceKey === undefined ||
            sources.includes(filter.filterSourceKey)) &&
        (filter.key !== undefined ||
            (!filterSources.has(filter.sourceKey) &&
                !selectedSources.includes(filter.sourceKey))))
        .map(filter => filter.key !== undefined && selectedSources.includes(filter.sourceKey)
        ? { ...filter, includeWindow: true }
        : filter);
}
/** Literal list filters and count filters share exact scalar/array membership. */
function appBaseBindingLiteralFilterQuery(field, key) {
    const keys = typeof key === 'string' ? [key] : [...new Set(key)];
    if (field === '@id')
        return { rowIds: keys };
    return {
        filter: {
            type: 'group',
            op: 'or',
            conditions: ['isOneOf', 'containsOneOf'].map(fn => ({
                type: 'filter',
                left: { type: 'ref', name: field },
                function: fn,
                args: [{ type: 'literal', value: keys }],
            })),
        },
    };
}
/** Read literal lists in one OR query for columns and one for row IDs. When
 * their union holds more than one window, each list reads its own window
 * instead, from `offsets` (by `JSON.stringify([field, key])`, as the
 * binding runtime names it; 0 when absent), and names it in
 * `literalWindows`. Runs as source text: no module values.
 * Shared by SSR and the Bridge; dependencies are passed for serialization. */
async function readAppBaseBindingLiteralLists(read, filters, literalQuery, windowRows, offsets = {}) {
    const fields = new Map();
    for (const filter of filters) {
        if (filter.key === undefined)
            throw new Error('Literal list query requires literal keys');
        const keys = fields.get(filter.field) ?? [];
        keys.push(filter.key);
        fields.set(filter.field, keys);
    }
    if (!fields.size)
        throw new Error('Literal list query requires filters');
    const queries = [];
    const conditions = [];
    for (const [field, keys] of fields) {
        const query = literalQuery(field, keys);
        if (query.rowIds)
            queries.push(query);
        else if (query.filter)
            conditions.push(...query.filter.conditions);
    }
    // The portable filter contract allows at most 32 conditions per group.
    // Nest each field's two membership checks to keep one OR across all fields.
    if (conditions.length) {
        const fieldGroups = [];
        for (let i = 0; i < conditions.length; i += 2)
            fieldGroups.push({
                type: 'group',
                op: 'or',
                conditions: conditions.slice(i, i + 2),
            });
        let groups = fieldGroups;
        while (groups.length > 32) {
            const parents = [];
            for (let i = 0; i < groups.length; i += 32)
                parents.push({
                    type: 'group',
                    op: 'or',
                    conditions: groups.slice(i, i + 32),
                });
            groups = parents;
        }
        queries.unshift({
            filter: groups.length === 1
                ? groups[0]
                : { type: 'group', op: 'or', conditions: groups },
        });
    }
    /** A page of the source, checked; null when the source is unavailable. */
    const pageOf = (result) => {
        const page = result;
        if (page?.status === 'unavailable')
            return null;
        if (!page || !Array.isArray(page.columns) || !Array.isArray(page.rows))
            throw new Error('Binding dataset read: invalid dataset');
        if (typeof page.totalCount !== 'number' ||
            !Number.isSafeInteger(page.totalCount) ||
            page.totalCount < page.rows.length)
            throw new Error('Binding dataset read: invalid dataset');
        for (const row of page.rows) {
            if (!row ||
                typeof row.id !== 'string' ||
                !row.cells ||
                typeof row.cells !== 'object')
                throw new Error('Binding dataset read: invalid row');
        }
        return page;
    };
    const rows = new Map();
    let first;
    let whole = true;
    for (const query of queries) {
        const result = await read(query);
        const page = pageOf(result);
        if (!page)
            return Object.assign({}, result, { literalRows: undefined });
        first ??= result;
        for (const row of page.rows)
            rows.set(row.id, row);
        if (page.totalCount > page.rows.length || rows.size > windowRows) {
            whole = false;
            break;
        }
    }
    let literalWindows;
    if (!whole) {
        // More rows than one window: each list reads the window it shows.
        rows.clear();
        literalWindows = {};
        for (const [field, keys] of fields)
            for (const key of new Set(keys)) {
                const windowKey = JSON.stringify([field, key]);
                const offset = offsets[windowKey] ?? 0;
                const result = await read({ offset, ...literalQuery(field, key) });
                const page = pageOf(result);
                if (!page)
                    return Object.assign({}, result, { literalRows: undefined });
                for (const row of page.rows)
                    rows.set(row.id, row);
                literalWindows[windowKey] = {
                    offset,
                    totalCount: page.totalCount,
                    rowIds: page.rows.map(row => row.id),
                };
            }
    }
    if (filters.some(filter => filter.includeWindow))
        return Object.assign({}, await read({}), {
            literalRows: [...rows.values()],
            ...(literalWindows ? { literalWindows } : {}),
        });
    return Object.assign({}, first, {
        rows: [...rows.values()],
        totalCount: rows.size,
        nextCursor: undefined,
        literalRows: undefined,
        ...(literalWindows ? { literalWindows } : {}),
    });
}
/**
 * The literal-key lists a list filtered by another row reads: one key per
 * distinct value of the filter rows, so each list of a row reads its own
 * window like a literal list (readAppBaseBindingLiteralLists). When the
 * filter source shows several rows and none is selected, a list inside their
 * list filters by each of them. Null when there is no filter row or a value
 * is not scalar: no row of the list can match then, and it reads its plain
 * window. Shared by the server render and the Bridge, delivered to the frame
 * as source text: no module values at runtime.
 */
function appBaseBindingRowFilterKeys(filter, filterPage, selectedRowId) {
    const page = filterPage;
    if (!page || page.status === 'unavailable' || !Array.isArray(page.rows))
        return null;
    const rows = page.rows;
    const filterRows = selectedRowId !== undefined
        ? rows.filter(item => item?.id === selectedRowId)
        : rows;
    const valueOf = (row) => {
        let value = row?.id;
        if (filter.value !== '@id') {
            // Every querySource cell is { value }.
            const cells = row?.cells;
            const cell = cells &&
                typeof cells === 'object' &&
                Object.prototype.hasOwnProperty.call(cells, filter.value)
                ? cells[filter.value]
                : undefined;
            value =
                cell && typeof cell === 'object' && 'value' in cell
                    ? cell.value
                    : undefined;
        }
        return ['string', 'number', 'boolean'].includes(typeof value) &&
            value !== ''
            ? String(value)
            : null;
    };
    const values = [...new Set(filterRows.map(valueOf))];
    if (!values.length || values.includes(null))
        return null;
    return values.map(key => ({
        sourceKey: filter.sourceKey,
        field: filter.field,
        key,
    }));
}
/** One bounded window per source, shared by SSR adoption and the Bridge.
 * Lists page explicitly through querySource; bindings never assemble a source.
 * Delivered to the frame as source text: no module values at runtime. */
async function readAppBaseBindingDatasets(read, sources, options, resolveFixedRow, 
/** Passed in, never imported: this function runs in the frame as source
 * text. Without it row-dependent lists read their plain window. */
rowFilterKeys, literalFilterQuery = appBaseBindingLiteralFilterQuery, readLiteralLists = readAppBaseBindingLiteralLists, windowRows = exports.APP_BASE_BINDING_WINDOW_ROWS, selectListFilters = selectAppBaseBindingListFilters) {
    const fail = (reason) => {
        throw new Error(`Binding dataset read: ${reason}`);
    };
    if (!sources.length ||
        sources.length > 32 ||
        new Set(sources).size !== sources.length)
        return fail('invalid sources');
    if (options.signal?.aborted)
        return fail('aborted');
    const fixedSelection = options.fixedSelection;
    if (fixedSelection && !resolveFixedRow)
        return fail('fixed row resolver is missing');
    const selectionOf = (sourceKey) => {
        const selectedRowId = options.selectedRows?.[sourceKey];
        const dynamic = options.dynamicSelection?.sourceKey === sourceKey
            ? options.dynamicSelection
            : undefined;
        const fixed = !selectedRowId &&
            !dynamic &&
            fixedSelection?.selection.sourceKey === sourceKey
            ? fixedSelection
            : undefined;
        return { selectedRowId, dynamic, fixed };
    };
    // A list narrowed by its filter waits for the row it filters by; a row
    // chosen by the route keeps its own query.
    const filtered = new Map();
    for (const filter of selectListFilters(options.listFilters ?? [], sources, sources.filter(source => {
        const selection = selectionOf(source);
        return Boolean(selection.selectedRowId || selection.dynamic || selection.fixed);
    })))
        filtered.set(filter.sourceKey, [
            ...(filtered.get(filter.sourceKey) ?? []),
            filter,
        ]);
    // The window a literal list is scrolled to: the runtime names it
    // `<source>:<JSON.stringify([field, key])>`.
    const literalOffsets = (sourceKey, filters) => {
        const offsets = Object.create(null);
        for (const filter of filters) {
            const key = JSON.stringify([filter.field, filter.key]);
            const offset = options.offsets?.[`${sourceKey}:${key}`];
            if (offset !== undefined)
                offsets[key] = offset;
        }
        return offsets;
    };
    const readPages = (keys, response) => Promise.all(keys.map(async (sourceKey) => {
        const { selectedRowId, dynamic, fixed } = selectionOf(sourceKey);
        const filters = filtered.get(sourceKey);
        const filter = filters?.[0];
        // A list filtered by another row reads one literal window per value
        // of the rows it filters by.
        const literalFilters = filter?.key !== undefined
            ? filters
            : filter && filter.filterSourceKey !== undefined && rowFilterKeys
                ? rowFilterKeys(filter, response[filter.filterSourceKey], options.selectedRows?.[filter.filterSourceKey])
                : null;
        const literal = Boolean(literalFilters?.length);
        const windowed = !selectedRowId && !dynamic && !fixed && !literal;
        const query = {
            offset: windowed ? (options.offsets?.[sourceKey] ?? 0) : 0,
            ...(selectedRowId ? { limit: 1 } : dynamic ? { limit: 2 } : {}),
            ...(selectedRowId ? { rowIds: [selectedRowId] } : {}),
            ...(dynamic && 'owner' in dynamic ? { owner: dynamic.owner } : {}),
            ...(dynamic && 'columnId' in dynamic
                ? {
                    predicates: [
                        {
                            type: 'column',
                            propertyId: dynamic.columnId,
                            operator: 'equals',
                            value: dynamic.value,
                        },
                    ],
                }
                : {}),
            // The query narrows the candidates; the exact row is chosen below
            // with the same rule as the published page.
            ...(fixed
                ? {
                    predicates: [
                        {
                            type: 'column',
                            propertyId: fixed.keyColumnId,
                            operator: 'contains',
                            value: fixed.selection.type === 'fixed-slug'
                                ? fixed.selection.slug.trim()
                                : fixed.selection.value,
                        },
                    ],
                }
                : {}),
        };
        const value = literal && literalFilters
            ? await readLiteralLists(literalQuery => read(sourceKey, Object.keys(literalQuery).length
                ? { offset: 0, ...literalQuery }
                : query), literalFilters, literalFilterQuery, windowRows, literalOffsets(sourceKey, literalFilters))
            : await read(sourceKey, query);
        return [
            sourceKey,
            value,
            Boolean(dynamic),
            fixed,
            windowed ? query.offset : undefined,
        ];
    }));
    const response = Object.create(null);
    let bytes = 0;
    const adopt = (pages) => {
        if (options.signal?.aborted)
            return fail('aborted');
        for (const [source, entry, dynamic, fixed, offset] of pages) {
            if (!entry || typeof entry !== 'object' || Array.isArray(entry))
                return fail('missing dataset');
            const page = entry;
            if (!Array.isArray(page.rows) || !Array.isArray(page.columns))
                return fail('invalid dataset');
            if (page.status === 'unavailable') {
                response[source] = page;
                continue;
            }
            if (dynamic &&
                (page.rows.length !== 1 ||
                    typeof page.totalCount !== 'number' ||
                    page.totalCount !== 1))
                return fail('dynamic route is missing or ambiguous');
            // Literal lists that read their own windows hold one window each.
            const literalWindows = page.literalWindows;
            const windows = Math.max(1, Object.keys(literalWindows ?? {}).length);
            if (page.rows.length > windowRows * windows)
                return fail('window row budget exceeded');
            if (page.literalRows !== undefined &&
                (!Array.isArray(page.literalRows) ||
                    page.literalRows.length > windowRows * windows))
                return fail('literal row budget exceeded');
            let rows = page.rows;
            if (fixed && resolveFixedRow) {
                // Never choose from a truncated window.
                if (typeof page.totalCount !== 'number' ||
                    page.totalCount > page.rows.length)
                    return fail('fixed row candidates exceed one window');
                // Every querySource cell is { value }.
                const cellValue = (row, columnId) => {
                    const cells = row?.cells;
                    const cell = cells && typeof cells === 'object'
                        ? cells[columnId]
                        : undefined;
                    return cell && typeof cell === 'object' && 'value' in cell
                        ? cell.value
                        : undefined;
                };
                const localeColumnId = fixed.localeColumnId;
                // A language column the App may not read leaves every row neutral.
                const languageReadable = localeColumnId !== null &&
                    page.columns.some(column => column?.id === localeColumnId);
                const resolution = resolveFixedRow(fixed.selection, page.rows.map((row) => ({
                    row,
                    key: cellValue(row, fixed.keyColumnId),
                    locale: languageReadable && localeColumnId !== null
                        ? cellValue(row, localeColumnId)
                        : undefined,
                })), fixed.locale);
                if (resolution.status === 'missing')
                    return fail('fixed row is missing');
                if (resolution.status === 'ambiguous')
                    return fail(`fixed row is ambiguous: ${resolution.count} rows match and ${languageReadable
                        ? 'share the language'
                        : 'no readable language column separates them'}`);
                rows = [resolution.candidate.row];
            }
            const projected = {
                status: 'available',
                columns: page.columns,
                rows,
                // A listed source names its window: where it begins, of how many rows.
                ...(offset !== undefined && typeof page.totalCount === 'number'
                    ? { offset, totalCount: page.totalCount }
                    : {}),
                ...(literalWindows ? { literalWindows } : {}),
                ...(page.literalRows === undefined
                    ? {}
                    : { literalRows: page.literalRows }),
            };
            response[source] = projected;
            bytes += new TextEncoder().encode(JSON.stringify(projected)).length;
            if (bytes > 2 * 1024 * 1024)
                return fail('route byte budget exceeded');
        }
    };
    // A list filtered by a literal key waits for nothing.
    const waits = (sourceKey) => filtered.get(sourceKey)?.[0]?.filterSourceKey !== undefined;
    adopt(await readPages(sources.filter(sourceKey => !waits(sourceKey)), response));
    if (sources.some(waits))
        adopt(await readPages(sources.filter(waits), response));
    return response;
}
/** Exact scalar/array membership, evaluated by the existing query aggregate
 * independently of the row window. Embedded in both Bridges. */
async function readAppBaseBindingCounts(read, counts, datasets, literalFilterQuery = appBaseBindingLiteralFilterQuery) {
    if (counts.length > 32)
        throw new Error('Binding count budget exceeded');
    await Promise.all(counts.map(async (count) => {
        const dataset = datasets[count.sourceKey];
        if (!dataset)
            throw new Error('Missing binding count dataset');
        if (dataset.status === 'unavailable')
            return;
        const { field, key } = count;
        if ((field === null) !== (key === null) || field === '' || key === '')
            throw new Error('Invalid binding count filter');
        const result = (await read(count.sourceKey, {
            offset: 0,
            limit: 1,
            ...(field !== null && key !== null
                ? literalFilterQuery(field, key)
                : {}),
            aggregates: [{ id: 'binding-count', type: 'count' }],
        }));
        const aggregate = result?.aggregates?.find(item => item.id === 'binding-count');
        if (!aggregate ||
            aggregate.type !== 'count' ||
            !aggregate.complete ||
            typeof aggregate.value !== 'number' ||
            !Number.isSafeInteger(aggregate.value) ||
            aggregate.value < 0)
            throw new Error('Invalid binding count aggregate');
        dataset.counts ??= Object.create(null);
        dataset.counts[JSON.stringify([count.field, count.key])] =
            aggregate.value;
    }));
}
